import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import Plus from '../../../assets/plusWhite.svg';
import { useSmartLink } from '../../../hooks/useSmartLink';
import { useNavigate } from 'react-router';
import SettingIcon from '../../../assets/Setting.svg';
import { ProgressSpinner } from 'primereact/progressspinner';
import { Paginator, PaginatorPageChangeEvent } from 'primereact/paginator';
import Polygon from '../../../assets/Polygon.svg';
import PolygonActive from '../../../assets/PolygonActive.svg';
import { FormDate, FormInput, FormSelect } from 'ui-kit/SmartForm';
import IntlStore from 'stories/IntlStore';
import { Form } from 'react-final-form';
import { useArtists } from 'components/NewRelease/hooks/useArtists';
import { Artist } from 'types/artist';
import { useLabels } from 'components/Labels/hooks/useLabels';
import { Label } from 'types/label';
import { useReleases } from 'components/Analytics/Filters/hooks/useReleases';

interface SmartLinkListProps { }

export const SmartLinkList: React.FC<SmartLinkListProps> = () => {
  const { data, getSmartLinks, deleteSmartLink, loading } = useSmartLink();
  const navigate = useNavigate();
  const { artists } = useArtists();
  const { labels } = useLabels()
  const { releases } = useReleases()
  const [first, setFirst] = useState<number>(0);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [pageCount, setPageCount] = useState<number>(10);
  const [totalRecords, setTotalRecords] = useState<number>(0);
  const [openSettingId, setOpenSettingId] = useState<string | null>(null);
  const [open, setOpen] = useState(false);
  const [isOpenMain, setIsOpenMain] = useState(false);
  const formRef = React.useRef<any>(null);

  useEffect(() => {
    const fetchData = async () => {
      const res = await getSmartLinks(currentPage, pageCount);
      setTotalRecords(res.total);
    };
    fetchData();
  }, [currentPage, pageCount]);


  const onPageChange = async (event: PaginatorPageChangeEvent) => {
    const newPage = Math.floor(event.first / event.rows) + 1;
    setCurrentPage(newPage);
    setFirst(event.first);
    setPageCount(event.rows);
    const res = await getSmartLinks(newPage, event.rows);
    setTotalRecords(res.total);
  };

  const toggleSetting = (id: string) => {
    setOpenSettingId(prev => (prev === id ? null : id));
  };

  const onSubmit = async (values: any) => {
    try {
      const formatDate = (date: any) => {
        if (!date) return undefined;
  
        const d = new Date(date);
        if (isNaN(d.getTime())) return undefined;
  
        return d.toISOString().split("T")[0];
      };
  
      const filters = {
        release_date_from: formatDate(values["Release date"]?.from),
        release_date_to: formatDate(values["Release date"]?.to),
        ean_upc: values.ean || undefined,
        isrc: values.ISRC || undefined,
        artist_ids: values.artists ? [values.artists] : undefined,
        label_id: values.label || undefined,
        release_title: values.ReleaseTitle || undefined,
      };
  
  
      const result = await getSmartLinks(1, 10, filters);  
      setOpen(!open)
    } catch (error) {
      console.error("❌ Error submitting filters:", error);
    }
  };
  
  
  

  if (loading)
    return (
      <Loading>
        <ProgressSpinner />
      </Loading>
    );

  return (
    <Content>
      <HearderBox>
        <SearchBox>
          <Search onClick={() => setOpen(!open)}>
            <span>Release Filters</span>
            {!open ? <Polygon /> : <PolygonActive />}
          </Search>
          <span
            style={{ cursor: 'pointer', marginLeft: '10px', color: 'rgba(0, 0, 0, 0.5)' }}
            onClick={ async () => {
              if (formRef.current) {
                formRef.current.reset();
              }
              const result = await getSmartLinks(1, 10, {})
            }}
          >
            Reset
          </span>
        </SearchBox>

        {open && (
          <DropdownDiv>
            <Form
              initialValues={{
                "Release date": '',
                ean: '',
                ISRC: '',
                artists: [],
                ReleaseTitle: '',
                label: ''
              }}
              onSubmit={onSubmit}
              render={({ handleSubmit, values, form }) => {
                formRef.current = form;
                const isFormEmpty =
                !values["Release date"] &&
                !values.ean &&
                !values.ISRC &&
                (!values.artists || values.artists.length === 0) &&
                !values.ReleaseTitle &&
                !values.label;
                return (
                  <form onSubmit={handleSubmit}>
                    <FormBox>
                      <FormDate
                        name="Release date"
                        label={IntlStore.intl.formatMessage({
                          id: 'NewRelease.ReleaseInfo.OriginalReleaseDate',
                          defaultMessage: 'Release date',
                        })}
                        tooltip="tooltip"
                        placeholder="Select date"
                      />

                      <FormInput name="ean" label="EAN / UPC" tooltip="tooltip" />

                      <FormInput name="ISRC" label="ISRC" tooltip="tooltip" />

                      <FormSelect
                        id="artists"
                        name="artists"
                        label={IntlStore.intl.formatMessage({
                          id: 'NewRelease.ReleaseInfo.Artist',
                          defaultMessage: 'artists',
                        })}
                        placeholder={IntlStore.intl.formatMessage({
                          id: 'NewRelease.ReleaseInfo.SmartLinkFilterArtits.Placeholder',
                          defaultMessage: 'Select the artists',
                        })}
                        items={
                          artists?.map((val: Artist) => ({
                            value: val.id,
                            label: val.name,
                          })) ?? []
                        }
                        values={values}
                      />

                      <FormSelect
                        id="ReleaseTitle"
                        name="ReleaseTitle"
                        label={IntlStore.intl.formatMessage({
                          id: 'NewRelease.ReleaseInfo.Releasetitle',
                          defaultMessage: 'Release title',
                        })}
                        placeholder={IntlStore.intl.formatMessage({
                          id: 'NewRelease.ReleaseInfo.SmartLinkFilterTitle.Placeholder',
                          defaultMessage: 'Enter the release title',
                        })}
                        items={
                          releases?.map((val: any) => ({
                            value: val.id,
                            label: val.title ?? 'Untitled',
                          })) ?? []
                        }
                        values={values}
                      />

                      <FormSelect
                        id="label"
                        name="label"
                        label={IntlStore.intl.formatMessage({
                          id: 'NewRelease.ReleaseInfo.Label',
                          defaultMessage: 'Label',
                        })}
                        placeholder={IntlStore.intl.formatMessage({
                          id: 'NewRelease.ReleaseInfo.SmartLinkFilterLabel.Placeholder',
                          defaultMessage: 'Select the label',
                        })}
                        items={
                          labels?.map((val: Label) => ({
                            value: val.id,
                            label: val.name,
                          })) ?? []
                        }
                        values={values}
                      />

                      <SendBox>
                        <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                          <Clear onClick={() => form.reset()}>Clear</Clear>
                          <Send type="submit" disabled={isFormEmpty}>Send</Send>
                        </div>
                      </SendBox>
                    </FormBox>
                  </form>
                );
              }}
            />
          </DropdownDiv>
        )}

        <AddBox onClick={() => navigate('/select-smart-links')}>
          <Plus /> Create a smart link
        </AddBox>
      </HearderBox>

      <HeaderBar>
        <Title>Release</Title>
        <Title>
          <span>Views</span>
          <span>Clicks</span>
        </Title>
      </HeaderBar>

      <ListBox>
        {data?.map((value: any) => (
          <Row key={value.id}>
            <Box>
              <ImgBox src={value.release.cover_url} />
              <TitleBox>{value.release.title}</TitleBox>
            </Box>

            <SettingBox>
              <ViewBox>1 245</ViewBox>
              <ClickBox>743</ClickBox>
              <div style={{ position: 'relative' }}>
                <Setting onClick={() => toggleSetting(value.id)}>
                  <SettingIcon />
                </Setting>

                {openSettingId === value.id && (
                  <DropdownMenu>
                    <div onClick={() =>
                      navigate(`/smart-link-Analytics/${value.id}`)
                    }>Analytics</div>
                    <div
                      onClick={() =>
                        navigate(`/smart-link-creation/${value.id}/edit`)
                      }
                    >
                      Edit Smart Link
                    </div>
                    <div onClick={() => deleteSmartLink(value.id)}>Delete</div>
                  </DropdownMenu>
                )}
              </div>
            </SettingBox>
          </Row>
        ))}
      </ListBox>

      <PaginatorWrapper>
        <Paginator first={first} rows={pageCount} totalRecords={totalRecords} rowsPerPageOptions={[5, 10, 20, 30]} onPageChange={onPageChange} />

      </PaginatorWrapper>
    </Content>

  );
};

/* --- Styled Components --- */

const Content = styled.div`
  width: 100%;
  height: 100vh;
  display: flex;
  background: rgba(255, 255, 255, 1);
  padding: 25px 21px 21px 23px;
  flex-direction: column;
  // justify-content: space-between;
  gap: 15px;
`;

const HearderBox = styled.div`
  width: 100%;
  height: 31px;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const SearchBox = styled.div`
  display: flex;
  align-items: center;
  gap: 5px;
`;

const AddBox = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  font-family: SF Pro Display;
  font-weight: 600;
  font-size: 13px;
  line-height: 18px;
  color: rgba(103, 119, 255, 1);
  cursor: pointer;
`;

const HeaderBar = styled.div`
  width: 100%;
  height: 39px;
  background: rgba(246, 246, 246, 1);
  border-radius: 10px;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const Title = styled.div`
  font-family: SF Pro Display;
  font-weight: 600;
  font-size: 14px;
  line-height: 18px;
  color: rgba(124, 124, 124, 1);
  padding: 0 99px 0 19px;
  display: flex;
  gap: 90px;
`;

const ListBox = styled.div`
  width: 100%;
  height: auto;
`;

const Row = styled.div`
  width: 100%;
  padding: 15px 17px;
  border-bottom: 1px solid rgba(224, 224, 224, 1);
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const ImgBox = styled.img`
  width: 75px;
  height: 75px;
  border-radius: 8px;
`;

const Box = styled.div`
  height: 75px;
  display: flex;
  align-items: center;
  gap: 34px;
`;

const TitleBox = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 5px;
`;

const SettingBox = styled.div`
  width: 245px;
  display: flex;
  gap: 53px;
`;

const ViewBox = styled.div``;

const ClickBox = styled.div`
  padding-left: 40px;
`;

const Setting = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 33px;
  width: 33px;
  &:hover {
    border-radius: 50%;
    background: rgba(226, 229, 255, 1);
    cursor: pointer;
  }
`;

const DropdownMenu = styled.div`
  position: absolute;
  top: 40px;
  right: 0;
  width: 160px;
  background: white;
  border: 1px solid rgba(224, 224, 224, 1);
  border-radius: 8px;
  box-shadow: 0px 4px 8px rgba(0, 0, 0, 0.1);
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 18px 14px 18px 12px;
  div {
    display: flex;
    align-items: center;
    height: 39px;
    padding: 6px 12px;
    cursor: pointer;
    border-radius: 10px;
    font-family: SF Pro Display;
    font-weight: 500;
    font-size: 13px;
    color: rgba(0, 0, 0, 0.5);
    &:hover {
      color: rgba(0, 0, 0, 1);
      background: rgba(226, 229, 255, 1);
    }
  }
`;

const Loading = styled.div`
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: 1000;
`;

const PaginatorWrapper = styled.div`
  display: flex;
  justify-content: center;
  padding-top: 20px;
`;

const Search = styled.div`
  width: 122px;
  height: 31px;
  border: 1px solid rgba(164, 164, 164, 1);
  font-family: SF Pro Display;
  font-weight: 400;
  font-size: 15px;
  line-height: 20px;
  color: rgba(0, 0, 0, 1);
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 5px;
`;

const DropdownDiv = styled.div`
  position: absolute;
  width: 364px;
  min-height: 546px;
  top: 179px;
  left: 375px;
  border-radius: 10px;
  background: white;
  box-shadow: 0px 4px 8px rgba(0, 0, 0, 0.1);
  z-index: 100;
  display: flex;
  flex-direction: column;
  padding: 16px;
  gap: 10px;
`;

const FormBox = styled.div`
  display: flex;
  flex-direction: column;
  gap: 15px;
`;

const SendBox = styled.div`
  width:100%;
  display: flex;
  align-items: center;
  justify-content: flex-end;

`
const Clear = styled.div`
  font-family: SF Pro Display;
  font-weight: 600;
  font-style: Semibold;
  font-size: 13px;
  leading-trim: NONE;
  line-height: 18px;
  letter-spacing: 0%;
  text-align: center;
  vertical-align: middle;
  color:rgba(0, 0, 0, 1);
  cursor:pointer;
`

const Send = styled.button<{ disabled?: boolean }>`
  width: 61px;
  height: 35px;
  border-radius: 8px;
  display: flex;
  justify-content: center;
  align-items: center;
  background: ${({ disabled }) =>
    disabled ? "rgba(103, 119, 255, 0.5)" : "rgba(103, 119, 255, 1)"};
  font-family: SF Pro Display;
  font-weight: 600;
  font-size: 13px;
  line-height: 18px;
  color: white;
  border: none;
  outline: none;
  cursor: ${({ disabled }) => (disabled ? "not-allowed" : "pointer")};
  transition: background 0.2s ease;
`;
